import React, { useCallback } from 'react'

import {
  Button,
  ChevronDown,
  ChevronUp,
  Icon,
  RadioButtonChecked,
  RadioButtonUnchecked,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, shape, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'
import SolidgatePaymentSection from 'Pods/Shared/SolidgatePaymentSection/SolidgatePaymentSection'

import { NOOP } from 'Utils/stableDefaults'

const TRANSLATION_KEY_PREFIX = 'subscription_upgrade.payment_method'

function PaymentMethod({
  paymentMethod = null,
  updatePaymentData = null,
  useSavedMethod = true,
  isDifferentOpen = false,
  onSelectSaved = NOOP,
  onToggleDifferent = NOOP,
  onFormReady = NOOP,
  onFormValidityChange = NOOP,
  onPaymentProcessing = NOOP,
  onPaymentSuccess = NOOP,
  tracking = undefined,
  locale = 'en',
}) {
  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale],
  )

  const ToggleIcon = isDifferentOpen ? ChevronUp : ChevronDown

  return (
    <section className="payment-method">
      <h2 className="payment-method__title cgt-heading-300">
        {translate(`${TRANSLATION_KEY_PREFIX}.title`)}
      </h2>
      <p className="payment-method__subtitle cgt-body-base">
        {translate(`${TRANSLATION_KEY_PREFIX}.subtitle`)}
      </p>

      {paymentMethod && (
        <button
          type="button"
          className={`payment-method__saved${useSavedMethod ? ' payment-method__saved--selected' : ''}`}
          onClick={onSelectSaved}
          aria-pressed={useSavedMethod}
        >
          <Icon svgComponent={useSavedMethod ? RadioButtonChecked : RadioButtonUnchecked} color="dark" />
          <span className="payment-method__saved-value cgt-body-base-semibold">
            {paymentMethod}
          </span>
          <span className="payment-method__saved-badge cgt-caption-semibold">
            {translate(`${TRANSLATION_KEY_PREFIX}.saved_badge`)}
          </span>
        </button>
      )}

      {updatePaymentData && (
        <>
          <Button
            type="ghost-primary"
            size="small"
            actionType="button"
            customClass="payment-method__toggle"
            onClick={onToggleDifferent}
            aria-expanded={isDifferentOpen}
            aria-controls="upgrade-payment-section"
          >
            {translate(`${TRANSLATION_KEY_PREFIX}.use_different`)}
            <Icon svgComponent={ToggleIcon} size="small" />
          </Button>

          {isDifferentOpen && (
            <div id="upgrade-payment-section" className="payment-method__section">
              <SolidgatePaymentSection
                merchant={updatePaymentData.merchant}
                signature={updatePaymentData.signature}
                paymentIntent={updatePaymentData.paymentIntent}
                returnPath={updatePaymentData.returnPath}
                locale={locale}
                onFormReady={onFormReady}
                onFormValidityChange={onFormValidityChange}
                onPaymentProcessing={onPaymentProcessing}
                onPaymentSuccess={onPaymentSuccess}
                tracking={tracking}
                translationKeyPrefix="subscription_checkout"
              />
            </div>
          )}
        </>
      )}
    </section>
  )
}

PaymentMethod.propTypes = {
  paymentMethod: string,
  updatePaymentData: shape({
    merchant: string,
    signature: string,
    paymentIntent: string,
    returnPath: string,
  }),
  useSavedMethod: bool,
  isDifferentOpen: bool,
  onSelectSaved: func,
  onToggleDifferent: func,
  onFormReady: func,
  onFormValidityChange: func,
  onPaymentProcessing: func,
  onPaymentSuccess: func,
  tracking: shape({
    trackPayClick: func,
    trackSuccess: func,
    trackPaymentMethodSelection: func,
    trackFailure: func,
  }),
  locale: string,
}

export default PaymentMethod
